import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { KeyRound, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Self-service password change (Profile Settings → Account).
export default function ChangePasswordCard() {
  const { getAuthHeader } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (next.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (next !== confirm) {
      toast.error('New passwords do not match');
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/auth/change-password`,
        { current_password: current, new_password: next },
        { headers: getAuthHeader() },
      );
      toast.success('Password changed successfully');
      setCurrent(''); setNext(''); setConfirm('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not change password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="wm-card p-6 rounded-lg" data-testid="change-password-card">
      <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
        <KeyRound className="w-4 h-4 text-primary" /> Change Password
      </h3>
      <div className="space-y-3 max-w-md">
        <div>
          <Label className="text-foreground">Current password</Label>
          <Input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            className="mt-1 bg-input border-border text-foreground"
            data-testid="change-password-current"
          />
        </div>
        <div>
          <Label className="text-foreground">New password</Label>
          <Input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            placeholder="At least 8 characters"
            className="mt-1 bg-input border-border text-foreground"
            data-testid="change-password-new"
          />
        </div>
        <div>
          <Label className="text-foreground">Confirm new password</Label>
          <Input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="mt-1 bg-input border-border text-foreground"
            data-testid="change-password-confirm"
          />
        </div>
        <Button
          onClick={submit}
          disabled={saving || !current || !next || !confirm}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
          data-testid="change-password-submit"
        >
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : 'Update password'}
        </Button>
      </div>
    </div>
  );
}
